import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import * as appKitNetworks from '@reown/appkit/networks'
import { AppKitNetwork } from '@reown/appkit/networks'
import { ConnectMethod, createAppKit } from '@reown/appkit/react'
import { LANDING_PAGE } from 'config'
import { fallback, http, Transport } from 'viem'

import { getRpcFallbackUrls } from './getRpcFallbackUrls'
import { getUfarmChain } from './ufarmChain'

const app = import.meta.env.VITE_APP
const isIframe = window.location.href.includes('/iframe/')
const rpcUrl = import.meta.env.VITE_RPC_URL
let walletConfig: undefined | WagmiAdapter['wagmiConfig']

const isAppKitNetwork = (network: unknown): network is AppKitNetwork => {
  if (typeof network !== 'object' || network === null) return false

  const maybeNetwork = network as { id?: unknown; rpcUrls?: unknown }

  return (
    typeof maybeNetwork.id === 'number' && typeof maybeNetwork.rpcUrls === 'object' && maybeNetwork.rpcUrls !== null
  )
}

const knownNetworks: AppKitNetwork[] = (Object.values(appKitNetworks) as unknown[]).filter(isAppKitNetwork)

const getNetwork = (chainId: number) => {
  return knownNetworks.find((network) => network.id === chainId) ?? (getUfarmChain(chainId) as AppKitNetwork)
}

export const getConfig = async (chainId: number, networkChainIds: number[] = [chainId]) => {
  // AppKit retains its first adapter; every mount must use that same adapter's Wagmi state.
  if (walletConfig) return walletConfig
  const projectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID as string

  const isInvestor = app === 'investors'
  const isDarkTheme = String(import.meta.env.VITE_THEME_IS_DARK) === 'true'

  const metadata = {
    description: 'UFarm Digital',
    icons: [
      'https://media.licdn.com/dms/image/v2/D4D0BAQGN0-M4ejHgIw/company-logo_200_200/company-logo_200_200/0/1733083688235/ufarm_digital_logo?e=2147483647&v=beta&t=GxBX3o2LKMZYPDFC2D6aWTJgcEjaH-oMlOqdP-zjFBo',
    ],
    name: 'UFarm Digital',
    url: LANDING_PAGE,
  }

  const chainIds = Array.from(new Set([chainId, ...networkChainIds]))
  const networks = chainIds.map(getNetwork) as [AppKitNetwork, ...AppKitNetwork[]]
  const transports = networks.reduce<Record<number, Transport>>((acc, network) => {
    const rpcUrls = getRpcFallbackUrls(network.id === chainId ? rpcUrl : undefined, network.rpcUrls.default.http[0])
    acc[network.id] = fallback(rpcUrls.map((url) => http(url)))
    return acc
  }, {})

  const wagmiAdapter = new WagmiAdapter({
    networks,
    projectId,
    transports,
  })

  const investorConnectMethods = isIframe ? ['email'] : ['social', 'email', 'wallet']

  createAppKit({
    adapters: [wagmiAdapter],
    allowUnsupportedChain: false,
    allWallets: 'ONLY_MOBILE',
    defaultAccountTypes: { eip155: 'eoa' },
    enableNetworkSwitch: true,
    enableWalletGuide: false,
    features: {
      connectMethodsOrder: isInvestor ? (investorConnectMethods as ConnectMethod[]) : ['wallet'],
      emailShowWallets: !isInvestor,
    },
    metadata,
    networks,
    projectId,
    themeMode: isDarkTheme ? 'dark' : 'light',
    themeVariables: isDarkTheme
      ? {
          '--w3m-accent': '#2ADF8A',
        }
      : undefined,
  })

  walletConfig = wagmiAdapter.wagmiConfig
  return walletConfig
}
